import React, { useState, useRef, useEffect } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    billingPath,
    clearPendingPlan,
    isPaidPlanId,
    peekPendingPlan,
    setPendingPlan,
} from "@/lib/billingPlans";
import Button from "@/components/common/Button";
import { Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { classifyAuthError, type AuthIssueKind } from "@/lib/authMessages";

interface LoginPageProps { initialMode?: "signin" | "signup" | "reset" }

// On the sign-in and sign-up forms a missing or expired session is not a reset-link problem.
const describeAuthError = (err: unknown) => {
    const issue = classifyAuthError(err);
    return issue.kind === "expired_link"
        ? { kind: "other" as AuthIssueKind, text: "Your session has expired. Please sign in again." }
        : issue;
};

const LoginPage: React.FC<LoginPageProps> = ({ initialMode = "signin" }) => {
    const { signIn, signUp, resetPassword, loading, error, clearError } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Remember a plan chosen on the pricing page until sign-in or sign-up is done.
    useEffect(() => {
        const plan = searchParams.get("plan");
        if (isPaidPlanId(plan)) setPendingPlan(plan);
    }, [searchParams]);

    const goAfterAuth = () => {
        const plan = peekPendingPlan();
        if (plan) {
            clearPendingPlan();
            navigate(billingPath(plan));
        } else {
            navigate("/dashboard");
        }
    };

    const [mode, setMode] = useState<"signin" | "signup" | "reset">(initialMode);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        confirmPassword: "",
        fullName: "",
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const [localError, setLocalError] = useState("");
    const [authIssue, setAuthIssue] = useState<AuthIssueKind | null>(null);
    const [resetSent, setResetSent] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const captchaRef = useRef<HCaptcha>(null);
    const [captchaLoadError, setCaptchaLoadError] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);

    // A captcha token can only be used once, so always drop it together with the widget's answer.
    const resetCaptcha = () => {
        setCaptchaToken(null);
        captchaRef.current?.resetCaptcha();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError("");
        setAuthIssue(null);
        clearError();
        setResetSent(false);

        if (mode !== "reset" && mode === "signup" && !captchaToken) {
            setLocalError("Please complete the captcha verification.");
            return;
        }
        if (mode !== "reset" && mode === "signup" && captchaToken) {
            try {
                const verifyRes = await fetch(
                    `${(import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"}/api/auth/verify-captcha`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ token: captchaToken }),
                    },
                );
                const verifyData = await verifyRes.json();
                if (!verifyData.success) {
                    setLocalError(
                        "Captcha verification failed. Please try again.",
                    );
                    resetCaptcha();
                    return;
                }
            } catch {
                setLocalError("Could not verify captcha. Please try again.");
                return;
            }
        }

        try {
            if (mode === "signin") {
                await signIn(formData.email, formData.password);
                resetCaptcha();
                setFailedAttempts(0);
                goAfterAuth();
            } else if (mode === "signup") {
                if (!formData.fullName.trim()) {
                    setLocalError("Please enter your full name");
                    return;
                }
                if (formData.password.length < 6) {
                    setLocalError("Password must be at least 6 characters");
                    return;
                }
                if (formData.password !== formData.confirmPassword) {
                    setLocalError("Passwords do not match");
                    return;
                }
                await signUp(formData.email, formData.password, {
                    full_name: formData.fullName,
                });
                resetCaptcha();
                setFailedAttempts(0);
                goAfterAuth();
            } else if (mode === "reset") {
                await resetPassword(formData.email);
                setResetSent(true);
            }
        } catch (err: any) {
            // Never show raw Supabase text; map it to a clear message.
            const issue = describeAuthError(err);
            setAuthIssue(issue.kind);
            setLocalError(issue.text);
            setFailedAttempts((prev) => prev + 1);
            if (mode === "signup") {
                resetCaptcha();
            }
        }
    };

    const resetForm = () => {
        setFormData({
            email: "",
            password: "",
            confirmPassword: "",
            fullName: "",
        });
    };

    const switchMode = (newMode: typeof mode) => {
        setCaptchaToken(null);
        setCaptchaLoadError(false);
        setMode(newMode);
        resetForm();
        setLocalError("");
        setAuthIssue(null);
        setResetSent(false);
    };

    // Used by the links in an error message: keeps the email the person typed.
    const goToMode = (newMode: typeof mode) => {
        setCaptchaToken(null);
        setCaptchaLoadError(false);
        setMode(newMode);
        setLocalError("");
        setAuthIssue(null);
        setResetSent(false);
        clearError();
    };

    const linkClass =
        "underline text-red-300 hover:text-white font-medium transition-colors";
    const displayError =
        localError || (error ? describeAuthError(error).text : "");

    return (
        <div
            className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{
                background:
                    "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
            }}
        >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
                    style={{
                        background:
                            "radial-gradient(circle, #eab308, transparent)",
                    }}
                />
                <div
                    className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
                    style={{
                        background:
                            "radial-gradient(circle, #64748b, transparent)",
                        animationDelay: "1s",
                    }}
                />
            </div>
            <div className="relative w-full max-w-md mx-4">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-gold-500 rounded-xl flex items-center justify-center shadow-lg">
                            <span className="text-slate-900 font-black text-lg">
                                T
                            </span>
                        </div>
                        <span className="text-2xl font-bold text-white tracking-tight">
                            TranslyxCAT
                        </span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold-500/30 bg-gold-500/10">
                        <div className="w-1.5 h-1.5 bg-gold-400 rounded-full animate-pulse" />
                        <span className="text-xs text-gold-400 font-medium">
                            Beta Access
                        </span>
                    </div>
                    <p className="mt-3 text-slate-400 text-sm">
                        {mode === "signin"
                            ? "Welcome back"
                            : mode === "signup"
                              ? "Create your account"
                              : "Reset your password"}
                    </p>
                </div>
                <div
                    className="rounded-2xl border border-white/10 p-8"
                    style={{
                        background: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(20px)",
                    }}
                >
                    {displayError && (
                        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                            <p>{displayError}</p>
                            {authIssue === "exists" && (
                                <p className="mt-1.5">
                                    <button
                                        type="button"
                                        onClick={() => goToMode("signin")}
                                        className={linkClass}
                                    >
                                        Sign in
                                    </button>
                                    {" · "}
                                    <button
                                        type="button"
                                        onClick={() => goToMode("reset")}
                                        className={linkClass}
                                    >
                                        Reset password
                                    </button>
                                </p>
                            )}
                            {authIssue === "invalid" && (
                                <p className="mt-1.5">
                                    <button
                                        type="button"
                                        onClick={() => goToMode("reset")}
                                        className={linkClass}
                                    >
                                        Reset password
                                    </button>
                                </p>
                            )}
                        </div>
                    )}
                    {resetSent && (
                        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                            Password reset email sent. Check your inbox.
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {mode === "signup" && (
                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        placeholder="Your full name"
                                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/50 transition-all"
                                    />
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="you@example.com"
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/50 transition-all"
                                />
                            </div>
                        </div>
                        {mode !== "reset" && (
                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                    Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="••••••••"
                                        className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/50 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                        {mode === "signup" && (
                            <div>
                                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        placeholder="••••••••"
                                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500/50 transition-all"
                                    />
                                </div>
                            </div>
                        )}
                        {mode === "signin" && (
                            <div className="text-right">
                                <button
                                    type="button"
                                    onClick={() => switchMode("reset")}
                                    className="text-xs text-slate-400 hover:text-gold-400 transition-colors"
                                >
                                    Forgot password?
                                </button>
                            </div>
                        )}
                        {mode !== "reset" && (mode === "signup") && (
                            <div className="flex flex-col items-center gap-2">
                                <HCaptcha
                                    ref={captchaRef}
                                    sitekey="577a2b9a-2fc2-4a22-b264-802680d054d0"
                                    onVerify={(token) => {
                                        setCaptchaToken(token);
                                        setCaptchaLoadError(false);
                                    }}
                                    onExpire={() => setCaptchaToken(null)}
                                    onError={() => {
                                        setCaptchaToken(null);
                                        setCaptchaLoadError(true);
                                    }}
                                />
                                {captchaLoadError && (
                                    <p className="text-xs text-red-400 text-center">
                                        The security check didn&apos;t load.
                                        Please refresh the page or try another
                                        browser.
                                    </p>
                                )}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-2.5 px-4 bg-gold-500 hover:bg-gold-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-xl text-sm transition-all shadow-lg"
                        >
                            {loading
                                ? "Please wait..."
                                : mode === "signin"
                                  ? "Sign In"
                                  : mode === "signup"
                                    ? "Create Account"
                                    : "Send Reset Link"}
                        </button>
                    </form>
                    <div className="mt-6 text-center text-sm text-slate-400">
                        {mode === "signin" && (
                            <>
                                Do not have an account?{" "}
                                <button
                                    type="button"
                                    onClick={() => switchMode("signup")}
                                    className="text-gold-400 hover:text-gold-300 font-medium transition-colors"
                                >
                                    Sign up
                                </button>
                            </>
                        )}
                        {mode === "signup" && (
                            <>
                                Already have an account?{" "}
                                <button
                                    type="button"
                                    onClick={() => switchMode("signin")}
                                    className="text-gold-400 hover:text-gold-300 font-medium transition-colors"
                                >
                                    Sign in
                                </button>
                            </>
                        )}
                        {mode === "reset" && (
                            <>
                                Remember your password?{" "}
                                <button
                                    type="button"
                                    onClick={() => switchMode("signin")}
                                    className="text-gold-400 hover:text-gold-300 font-medium transition-colors"
                                >
                                    Sign in
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <p className="text-center mt-6 text-xs text-slate-500">
                    By continuing, you agree to our{" "}
                    <a
                        href="#"
                        className="text-slate-400 hover:text-gold-400 transition-colors"
                    >
                        Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                        href="#"
                        className="text-slate-400 hover:text-gold-400 transition-colors"
                    >
                        Privacy Policy
                    </a>
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
